import { AWS_CONFIG } from '../config/awsConfig';

export interface ImageSize {
  key: string;
  contentUrl: string;
}

export interface UploadImageResponse {
  success: boolean;
  bucket: string;
  key: string;
  s3Url?: string;
  contentUrl: string; // Mantener para compatibilidad hacia atrás
  sizes?: {
    small?: ImageSize;
    medium?: ImageSize;
    large?: ImageSize;
  };
}

export interface UploadImageError {
  error: string;
  details?: string;
}

/**
 * Sube una imagen a S3 a través del backend
 * @param file - Archivo de imagen a subir
 * @returns Promise con la respuesta del servidor
 */
export async function uploadImageToS3(
  file: File
): Promise<UploadImageResponse> {
  const formData = new FormData();
  formData.append('image', file); // El nombre "image" debe coincidir con upload.single("image")

  const apiKey = process.env.REACT_APP_PET_CONTEST_ACCESS_KEY || '';
  const response = await fetch(`${AWS_CONFIG.API_URL}/api/upload-image`, {
    method: 'POST',
    headers: apiKey ? { 'x-api-key': apiKey } : undefined,
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Error al subir la imagen');
  }

  return data as UploadImageResponse;
}

export interface UploadParticipantMediaResponse {
  success: boolean;
  mediaType: 'image' | 'video';
  contentUrl: string;
  key: string;
  bucket?: string;
}

/**
 * Sube imagen o video de participación (desde dispositivo local).
 */
export async function uploadParticipantMedia(
  file: File,
  mediaRules?: {
    max_image_size_mb?: number;
    max_video_size_mb?: number;
  }
): Promise<UploadParticipantMediaResponse> {
  const formData = new FormData();
  formData.append('media', file);
  if (mediaRules?.max_image_size_mb != null) {
    formData.append('max_image_size_mb', String(mediaRules.max_image_size_mb));
  }
  if (mediaRules?.max_video_size_mb != null) {
    formData.append('max_video_size_mb', String(mediaRules.max_video_size_mb));
  }

  const apiKey = process.env.REACT_APP_PET_CONTEST_ACCESS_KEY || '';
  const response = await fetch(`${AWS_CONFIG.API_URL}/api/upload-participant-media`, {
    method: 'POST',
    headers: apiKey ? { 'x-api-key': apiKey } : undefined,
    body: formData,
  });

  let data: UploadParticipantMediaResponse | UploadImageError;
  try {
    data = await response.json();
  } catch {
    throw new Error(
      response.ok
        ? 'Respuesta inválida del servidor al subir el archivo'
        : `Error al subir el archivo (${response.status})`
    );
  }

  if (!response.ok) {
    throw new Error(data.error || data.details || 'Error al subir el archivo');
  }

  return data as UploadParticipantMediaResponse;
}
